import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { css } from '@emotion/react';
import { Box } from '@mui/material';
import useTranslation from 'next-translate/useTranslation';

import {
  OnboardingModalActions,
  useOnboardingModalActionsCompact,
} from '@/components/MainModal/OnboardingModalActions';
import { useAppSelector, useOnboardingTask, useTracking, useTraits } from '@/hooks';
import { selectCompanyId } from '@/redux/features/authSlice';
import { setPainPoint } from '@/redux/features/onboardingSlice';
import CompanyRequests from '@/requests/company.requests';
import { AmplitudeService } from '@/services/tracking/amplitude.service';
import { Button, LoadingButton } from '@/shared/Button';
import { FeaturesCard, FeaturesCardProps } from '@/shared/FeaturesCard';

import { FocusAreaStep, StepCommonProps, StepInfo } from '../../../types';

import styles from './FocusAreaStep.module.scss';

// Legacy key from the randomisation experiment. Still read so companies that
// were mid-onboarding when the experiment ended keep the order they were shown.
const TOP_CHALLENGE_ORDER_KEY = 'top-challenge-randomisation';

function hashSeed(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = Math.imul(hash, 31) + value.charCodeAt(i);
  }
  return hash | 0;
}

/** Deterministic order per company — stable across remounts without persistence. */
function seededShuffle<T extends { cardKey: string }>(items: T[], seed: string): T[] {
  return [...items].sort(
    (a, b) => hashSeed(`${ seed }:${ a.cardKey }`) - hashSeed(`${ seed }:${ b.cardKey }`)
      || a.cardKey.localeCompare(b.cardKey),
  );
}

const focusAreas: FeaturesCardProps[] = [
  {
    cardKey: 'accountability',
    title: 'Accountability & focus',
    color: '#C8FFE2',
    features: [
      'Know work is getting done',
      'Reduce idle time',
      'Optional screenshots',
    ],
  },
  {
    cardKey: 'productivity',
    title: 'Productivity & performance',
    color: '#FAE0FF',
    features: [
      'See where time goes',
      'Coach with data',
      'Capacity & workload planning',
    ],
  },
  {
    cardKey: 'timetrackingattendance',
    title: 'Time & attendance',
    color: '#FFFACE',
    features: [
      'Track worked hours',
      'Shifts & schedules',
      'Detect lateness & absence',
    ],
  },
  {
    cardKey: 'payroll',
    title: 'Payroll & billing',
    color: '#C7DDFF',
    features: [
      'Accurate timesheets',
      'Sync data with payroll',
      'Bill hours to clients & projects',
    ],
  },
  {
    cardKey: 'aiproductivityinsights',
    title: 'AI productivity insights',
    color: '#D0F5FF',
    features: [
      'See which AI tools are used',
      'Track AI spend & adoption',
      'Benchmark against peers',
    ],
  },
  {
    cardKey: 'softwarecost',
    title: 'Software cost & utilization',
    color: '#FFD4D4',
    features: [
      'See which tools are used',
      'Cut licence waste',
      'Reallocate spend',
    ],
  },
];

const FocusAreaStepComponent: FC<StepCommonProps> = ({ flow }) => {
  const { getNextStep, goToNextStep, goToPreviousStep } = useOnboardingTask(flow);
  const { trackOnboardingStep } = useTracking(flow);
  const { updateTraits } = useTraits();
  const companyId = useSelector(selectCompanyId);
  const { company } = useAppSelector(state => state.auth);
  const { painpoint: reduxPainPoint } = useAppSelector(state => state.onboarding);
  const backendPainPoint = company?.companySettings?.custom?.painpoint;
  const persistedPainPoint = reduxPainPoint || backendPainPoint;
  const { t } = useTranslation('onboarding');
  const compactActions = useOnboardingModalActionsCompact();

  const orderedFocusAreas = useMemo<FeaturesCardProps[]>(() => {
    const persistedOrder = company?.companySettings?.custom?.splitTest?.[TOP_CHALLENGE_ORDER_KEY];
    if (persistedOrder) {
      const keys = persistedOrder.split(',');
      const restored = keys
        .map(key => focusAreas.find(a => a.cardKey === key))
        .filter((a): a is FeaturesCardProps => Boolean(a));

      if (restored.length === focusAreas.length) return restored;
    }

    return seededShuffle(focusAreas, companyId);
  }, [company?.companySettings?.custom?.splitTest, companyId]);

  const initialSelectedCard = orderedFocusAreas.find(area => area.title === persistedPainPoint)?.cardKey ?? null;
  const [selectedCardKey, setSelectedCardKey] = useState<string | null>(initialSelectedCard);
  const [isCardClicked, setIsCardClicked] = useState<boolean>(!!initialSelectedCard);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();
  const checkIfIsOnboardingSSO = () => company?.splitTest?.some(st => st.name === 'google-auth' && st.value);
  const hasTrackedPainPoint = useRef(false);


  // Ensure redux is synced with backend on first load
  useEffect(() => {
    if (!reduxPainPoint && backendPainPoint) {
      dispatch(setPainPoint(backendPainPoint));
    }
  }, [reduxPainPoint, backendPainPoint, dispatch]);

  const trackSelectedPainPoint = useCallback(() => {
    if (hasTrackedPainPoint.current || !selectedCardKey) return;

    const selectedArea = orderedFocusAreas.find(area => area.cardKey === selectedCardKey);

    if (selectedArea) {
      trackOnboardingStep('Pain Point Selected Option', {
        painpoint: selectedArea.title,
      });
      hasTrackedPainPoint.current = true;
    }
  }, [selectedCardKey, orderedFocusAreas, trackOnboardingStep]);

  const onCardClicked = useCallback((cardKey: string, cardTitle: string) => {
    const nextStep = getNextStep();
    setIsCardClicked(true);
    setSelectedCardKey(cardKey);
    dispatch(setPainPoint(cardTitle));

    updateTraits({
      companyTraits: { onboarding_step: nextStep?.stepName },
      userTraits: {
        lead_pain_point: cardTitle,
        custom: { lead_pain_point: cardTitle },
      },
    });

    CompanyRequests.putCompanySettings(companyId, {
      custom: { painpoint: cardTitle },
    });

  }, [companyId, getNextStep, dispatch, updateTraits]);

  const renderedFocusAreas = useMemo(() =>
    orderedFocusAreas.map(({ title, cardKey, color, features }) => (
      <FeaturesCard
        key={ cardKey }
        cardKey={ cardKey }
        title={ title }
        features={ features }
        color={ color }
        onClick={ () => onCardClicked(cardKey, title) }
        className={ selectedCardKey === cardKey ? styles.Selected : undefined }
      />
    )),
  [orderedFocusAreas, onCardClicked, selectedCardKey]);

  const handleTrackingTypeExperiment = useCallback(async () => {
    goToNextStep();
  }, [goToNextStep]);

  const handleNextClick = async () => {
    if (isSubmitting || !isCardClicked) return;
    setIsSubmitting(true);

    trackSelectedPainPoint();

    const selectedArea = orderedFocusAreas.find(area => area.cardKey === selectedCardKey);
    if (companyId && selectedArea?.title) {
      AmplitudeService.groupIdentify('company', companyId, {
        painpoint: selectedArea.title,
      });
    }

    await handleTrackingTypeExperiment();

    setIsSubmitting(false);
  };

  return (
    <>
      <div className={ styles.FocusAreaStep }>
        { renderedFocusAreas }
      </div>

      <OnboardingModalActions>
        <Box
          sx={ compactActions ? { pt: 0 } : { pt: 3 } }
          display="flex"
          alignItems="flex-end"
          justifyContent={ checkIfIsOnboardingSSO() ? 'space-between' : 'flex-end' }
        >
          { checkIfIsOnboardingSSO() && (
            <Button
              variant="outlined"
              size="medium"
              uppercase
              onClick={ () => goToPreviousStep() }
            >
              { t('common:back') }
            </Button>
          ) }

          <LoadingButton
            variant="contained"
            type="button"
            disableElevation={ false }
            disabled={ !isCardClicked || isSubmitting }
            loading={ isSubmitting }
            onClick={ handleNextClick }
            css={ css`margin-bottom: 4px` }
          >
            { t('common:next') }
          </LoadingButton>
        </Box>
      </OnboardingModalActions>
    </>
  );
};

export const FocusAreaStepInfo: StepInfo<FocusAreaStep> = {
  Component: FocusAreaStepComponent,
  id: 'companyInfo2Step',
  stepName: 'Challenge Screen',
  title: 'onboarding:focus_area_step.title',
};
